import { z } from "zod";

import {
  PROCUREMENT_DERIVED_STATUS_LABELS,
  PROCUREMENT_DERIVED_STATUSES,
  type ProcurementDerivedStatus,
} from "@/api/procurement/procurement.types";

/*
 * SERV-3504: the queue's Status and Work Order chips, held in the URL (`status`, `wo`) so a
 * filtered queue can be sent as a link and survives a trip into a ticket and back, like `view`.
 *
 * A work order is kept as its id AND its number. The id is what filters; the number is only the
 * chip's label, kept so a cold link can show "WO-1042" without the work order being in the first
 * page of picker options. A hand-edited number only mislabels the chip: the server filters on the
 * id, inside the caller's tenant and scope, so it can never widen what is shown.
 */
export const QUEUE_WORK_ORDER_FILTER_SCHEMA = z.object({
  id: z.string().uuid(),
  n: z.string().max(100),
});
export type QueueWorkOrderFilter = z.infer<typeof QUEUE_WORK_ORDER_FILTER_SCHEMA>;

export const QUEUE_URL_FILTERS_SCHEMA = z.object({
  status: z.array(z.enum(PROCUREMENT_DERIVED_STATUSES)).max(100).optional().catch(undefined),
  wo: z.array(QUEUE_WORK_ORDER_FILTER_SCHEMA).max(100).optional().catch(undefined),
});
export type QueueUrlFilters = z.infer<typeof QUEUE_URL_FILTERS_SCHEMA>;

/* The chip deals in the labels it displays; built from the label map so a relabel still filters. */
export const STATUS_FILTER_LABELS: string[] = PROCUREMENT_DERIVED_STATUSES.map(
  (status) => PROCUREMENT_DERIVED_STATUS_LABELS[status],
);
const STATUS_BY_LABEL = new Map<string, ProcurementDerivedStatus>(
  PROCUREMENT_DERIVED_STATUSES.map((status) => [PROCUREMENT_DERIVED_STATUS_LABELS[status], status]),
);

export const statusesFromLabels = (labels: string[]): ProcurementDerivedStatus[] => [
  ...new Set(
    labels
      .map((label) => STATUS_BY_LABEL.get(label))
      .filter((status): status is ProcurementDerivedStatus => status !== undefined),
  ),
];

export const statusLabelsOf = (statuses: ProcurementDerivedStatus[] | undefined): string[] =>
  (statuses ?? []).map((status) => PROCUREMENT_DERIVED_STATUS_LABELS[status]);

/**
 * Turns the chip's labels back into work orders. `known` maps a label to its id, from the picker's
 * current options; `selected` is what the URL already holds, so a selection that has scrolled out
 * of the current search results is kept rather than dropped. A label neither knows is dropped.
 */
export const workOrdersFromLabels = (
  labels: string[],
  known: ReadonlyMap<string, string>,
  selected: QueueWorkOrderFilter[] | undefined,
): QueueWorkOrderFilter[] => {
  const selectedByLabel = new Map((selected ?? []).map((wo) => [wo.n, wo.id]));
  const byId = new Map<string, QueueWorkOrderFilter>();
  for (const label of labels) {
    const id = selectedByLabel.get(label) ?? known.get(label);
    if (id && !byId.has(id)) byId.set(id, { id, n: label });
  }
  return [...byId.values()];
};

/*
 * The library chip stops filtering its own list the moment the table is given a search callback
 * (FilterChip skips its local filter when `onSearch` is set), and that callback is per table, not
 * per column. The Work Order chip needs it for a server search, so the static chips filter here.
 */
export const filterItemsByQuery = (items: string[], query: string | undefined): string[] => {
  const q = query?.trim().toLowerCase();
  return q ? items.filter((item) => item.toLowerCase().includes(q)) : items;
};
